// Professional Onboarding.tsx with background videos
import React, { useState, useEffect, useRef } from 'react';
import { useHistory, Link } from 'react-router-dom';
import '../../styles/components/Onboarding.scss';
import { supabase } from '../../helpers/Supabase';
import logo from '../../assets/vivvy-logo.png';

const Onboarding: React.FC = () => {
  const history = useHistory();
  const [form, setForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentVideo, setCurrentVideo] = useState(1);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Background video rotation
  useEffect(() => {
    const videoInterval = setInterval(() => {
      setCurrentVideo(prev => prev === 4 ? 1 : prev + 1);
    }, 8000); // Change video every 8 seconds

    return () => clearInterval(videoInterval);
  }, []);

  // Video transition effect
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.style.opacity = '0';
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.style.opacity = '1';
        }
      }, 500);
    }
  }, [currentVideo]);

  // Floating particles effect
  useEffect(() => {
    const createParticle = () => {
      const particle = document.createElement('div');
      particle.className = 'particle';
      particle.style.left = Math.random() * 100 + '%';
      particle.style.top = Math.random() * 100 + '%';
      particle.style.width = Math.random() * 3 + 1 + 'px';
      particle.style.height = particle.style.width;
      particle.style.animationDelay = Math.random() * 6 + 's';
      particle.style.animationDuration = (Math.random() * 4 + 4) + 's';
      
      const container = document.querySelector('.onboarding-container');
      if (container) {
        container.appendChild(particle);
        
        // Remove particle after animation
        setTimeout(() => {
          if (particle.parentNode) {
            particle.parentNode.removeChild(particle);
          }
        }, 12000);
      }
    };

    // Create particles periodically
    const particleInterval = setInterval(createParticle, 3000);
    
    // Create initial particles
    for (let i = 0; i < 8; i++) {
      setTimeout(createParticle, i * 300);
    }

    return () => clearInterval(particleInterval);
  }, []);

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const validate = () => {
    if (!form.email || !form.password || !form.confirmPassword) {
      setError('All fields are required.');
      return false;
    }
    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match.');
      return false;
    }
    if (form.password.length < 8) {
      setError('Password must be at least 8 characters long.');
      return false;
    }
    return true;
  };

  const handleSignup = async () => {
    setError('');
    if (!validate()) return;
    setLoading(true);

    try {
      const { error } = await supabase.auth.signUp({
        email: form.email,
        password: form.password,
      });
      if (error) throw error;
      setSuccess(true);
    } catch (err: any) {
      console.error('[Signup Error]', err.message || err);
      setError(err.message || 'Signup failed.');
    } finally {
      setLoading(false);
    }
  };

  const checkEmailConfirmed = async () => {
    const { data } = await supabase.auth.getUser();
    if (data?.user?.email_confirmed_at) {
      history.push('/');
    } else {
      setError('Still waiting for email confirmation...');
    }
  };

  return (
    <div className="onboarding-container">
      {/* Background Videos */}
      <div className="background-videos">
        <video
          ref={videoRef}
          className={`background-video ${currentVideo === 1 ? 'active' : ''}`}
          autoPlay
          muted
          loop
          playsInline
          src={require('../../assets/bg-1.mp4')}
        />
        <video
          className={`background-video ${currentVideo === 2 ? 'active' : ''}`}
          autoPlay
          muted
          loop
          playsInline
          src={require('../../assets/bg-2.mp4')}
        />
        <video
          className={`background-video ${currentVideo === 3 ? 'active' : ''}`}
          autoPlay
          muted
          loop
          playsInline
          src={require('../../assets/bg-3.mp4')}
        />
        <video
          className={`background-video ${currentVideo === 4 ? 'active' : ''}`}
          autoPlay
          muted
          loop
          playsInline
          src={require('../../assets/bg-4.mp4')}
        />
        <div className="video-overlay"></div>
      </div>

      {/* Floating particles container */}
      <div className="floating-particles"></div>
      
      {/* Professional Content */}
      <div className="onboarding-content">
        <div className="brand-section">
          <img src={logo} alt="Vivvy Logo" className="brand-logo" />
          <div className="brand-text">
            <h1>Welcome to Vivvy</h1>
            <p>Transform your business with intelligent customer engagement</p>
          </div>
        </div>

        <div className="form-section">
          <div className="form-container">
            {!success ? (
              <>
                <div className="form-header">
                  <h2>Create Your Account</h2>
                  <p>Join hundreds of businesses already using Vivvy</p>
                </div>

                <form className="signup-form" onSubmit={(e) => { e.preventDefault(); handleSignup(); }}>
                  <div className="input-group">
                    <label htmlFor="email">Email Address</label>
                    <input 
                      id="email"
                      name="email" 
                      type="email" 
                      value={form.email} 
                      onChange={handleInput}
                      placeholder="Enter your business email"
                      aria-label="Email address"
                      required
                    />
                  </div>
                  
                  <div className="input-group">
                    <label htmlFor="password">Password</label>
                    <input 
                      id="password"
                      name="password" 
                      type="password" 
                      value={form.password} 
                      onChange={handleInput}
                      placeholder="Create a secure password"
                      aria-label="Password"
                      required
                    />
                    <div className="password-strength">
                      <div className={`strength-bar ${form.password.length >= 8 ? 'strong' : form.password.length >= 4 ? 'medium' : 'weak'}`}></div>
                      <span className="strength-text">
                        {form.password.length >= 8 ? 'Strong' : form.password.length >= 4 ? 'Medium' : 'Weak'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="input-group">
                    <label htmlFor="confirmPassword">Confirm Password</label>
                    <input 
                      id="confirmPassword"
                      name="confirmPassword" 
                      type="password" 
                      value={form.confirmPassword} 
                      onChange={handleInput}
                      placeholder="Confirm your password"
                      aria-label="Confirm password"
                      required
                    />
                  </div>

                  <button type="submit" className="submit-button" disabled={loading}>
                    {loading ? (
                      <>
                        <div className="loading-spinner"></div>
                        Creating Account...
                      </>
                    ) : (
                      'Create Account'
                    )}
                  </button>
                </form>

                <div className="form-footer">
                  <p>
                    Already have an account? <Link to="/" className="login-link">Sign In</Link>
                  </p>
                  {error && <div className="error-message">{error}</div>}
                </div>
              </>
            ) : (
              <div className="success-section">
                <div className="success-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                    <polyline points="22,4 12,14.01 9,11.01"/>
                  </svg>
                </div>
                <h2>Check Your Email</h2>
                <p>
                  We've sent a confirmation email to <strong>{form.email}</strong>
                </p>
                <p className="email-note">
                  Please verify your email address to complete your account setup.
                </p>
                <button onClick={checkEmailConfirmed} className="check-button">
                  Check Again
                </button>
                {error && <div className="error-message">{error}</div>}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
